// ─── useTableSort ────────────────────────────────────────────
// Hook réutilisable pour les tableaux triables au clic sur l'en-tête.
// Reproduit le pattern UX de la table d'appareils (Dashboard.jsx) :
//   - Clic sur un header → bascule asc/desc, ou nouvelle clé en asc
//   - Flèche ▲ ou ▼ sur la colonne active
//   - Couleur bleue + bold sur le header actif
//
// Usage typique :
//   const { sort, toggleSort, sortRows, headerProps, arrow } = useTableSort('name');
//   const rows = sortRows(filteredRows, (row, key) => row[key]);
//   <th {...headerProps('name')}>Nom{arrow('name')}</th>
//
// `accessor(row, key)` permet de mapper une clé de tri vers une valeur
// dérivée (ex: tri par "score" → row.security.score). Retourner null/undefined
// pour rejeter une ligne en fin de tri.

import { useState } from 'react';

export function useTableSort(initialKey = null, initialDir = 'asc') {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });

  const toggleSort = (key) => {
    if (!key) return;
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const sortRows = (rows, accessor) => {
    if (!sort.key || !Array.isArray(rows)) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = accessor || ((row, key) => row?.[key]);
    return [...rows].sort((a, b) => {
      const va = get(a, sort.key);
      const vb = get(b, sort.key);
      // null/undefined toujours en fin
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      if (va instanceof Date && vb instanceof Date) return (va.getTime() - vb.getTime()) * dir;
      // dates ISO : compare comme string mais met en avant le tri chronologique correct
      return String(va).localeCompare(String(vb), 'fr', { numeric: true, sensitivity: 'base' }) * dir;
    });
  };

  const headerProps = (key, extraStyle = {}) => ({
    onClick: key ? () => toggleSort(key) : undefined,
    style: {
      cursor: key ? 'pointer' : 'default',
      userSelect: 'none',
      color: key && sort.key === key ? '#60a5fa' : undefined,
      fontWeight: key && sort.key === key ? 600 : undefined,
      ...extraStyle,
    },
  });

  const arrow = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  return { sort, toggleSort, sortRows, headerProps, arrow };
}
